import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Loading from 'components/Loading';
import { useParams } from 'react-router-dom';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import { getImputationAsString } from 'routes/BudgetModule/config/hooks';
import InscriptionsGrid from 'routes/BudgetModule/components/shared/InscriptionsGrid/InscriptionsGrid';
import { getInscriptionsGridColumns } from 'routes/BudgetModule/components/shared/InscriptionsGrid/gridHelpers';
import { BudgetExpenseOrIncome } from 'business/constants';
import AuxiliaryTabs from 'components/SharedComponents/AuxiliaryTabs/AuxiliaryTabs';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

import { TRACKABLE_EVENTS } from 'helpers/trackerHelpers';
import {
    ALL_INSCRIPTION_FILE_PREFIX,
    getExcelFilename,
} from 'routes/BudgetModule/contexts/helpers';
import { useGetProjects } from 'routes/BudgetModule/shared/useGetProjects';
import {
    getOrderedColumns,
    getAllInscriptionsGridColumns,
} from './helpers/gridHelpers';
import InscriptionAlert from './components/InscriptionAlert';
import { useInscriptionAlertsManager } from './helpers/useInscriptionAlertsManager';
import TabActions from '../TabActions';
import { useGetSoftwares } from '../../BudgetProposal/Resource/hooks/softwaresIntegrations';
import { getExpenseIncomeValueFromString } from './helpers/allInscriptionsFetchUtils';
import { useGetAllInscriptions } from 'routes/BudgetModule/helpers/hooks/useGetInscriptions';
import { buildInscriptionsWithPhase } from 'routes/BudgetModule/helpers/inscriptionsFetchUtils';
import {
    useGetBudgetUserSettings,
    useMutateBudgetUserSettings,
} from '../AnalyseArguments/hooks/budgetUserSettings';
import { getAllInscriptionsUpdateBudgetSettingsCallback } from '../AnalyseArguments/helpers/settingsHelpers';
import { useSelectSecondaryBudgetAxisSourceTable } from 'routes/BudgetModule/components/BudgetWithProvider/SecondaryBudgetAxisSourceTableContext';
import { ImputationDimensionsContext } from 'routes/BudgetModule/components/BudgetWithProvider/ImputationDimensionsContext';
import InscriptionsHistoryModal from 'routes/BudgetModule/components/BudgetOverview/AllInscriptions/components/InscriptionsHistoryModal';

type Props = {
    budgetProposals: BudgetProposalT[];
    selectedBudgetName: string | null;
    year: number;
};

const StyOveriddenTabulatedChip = styled(AuxiliaryTabs, {
    name: 'StyOveriddenTabulatedChip',
})({
    height: '100%',
});

// TODO: I think we can get rid of some of these styles
const StyGrid = styled(Box, {
    name: 'StyGrid',
})({
    height: '100%',
    '&&& .ag-row-group': {
        display: 'flex',
        alignItems: 'center',
    },
    // By default, we do not display a min height in our inscription grid.
    // This style overrides it in order to display the "No rows" text when
    // there is no inscription to display
    '&&& div.ag-layout-auto-height .ag-center-cols-container': {
        minHeight: '20rem',
    },
    '&&& .ag-cell': {
        display: 'flex',
        alignItems: 'center',
        border: 'none',
        lineHeight: '16px',
        fontSize: '11px',
    },
});

export default function AllInscriptions({
    budgetProposals,
    selectedBudgetName,
    year,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const [isInscriptionHistoryModalOpen, setIsInscriptionHistoryModalOpen] =
        useState(false);
    const {
        tab,
        expenseOrIncome:
            expenseOrIncomeUrlParams = BudgetExpenseOrIncome.EXPENSE,
    } = useParams();
    const inscriptionHistoryModalTitle = t(
        'allInscriptions.inscriptionsHistoryModalTitle',
        {
            year,
            budgetName: selectedBudgetName,
            expenseOrIncome: t(`budget.${expenseOrIncomeUrlParams}`),
        },
    );
    const expenseOrIncomeValue = getExpenseIncomeValueFromString(
        expenseOrIncomeUrlParams,
    );

    const { budgetUserSettings } = useGetBudgetUserSettings();
    const { mutate: mutateSettings } = useMutateBudgetUserSettings();

    const { projects, isLoading: isProjectLoading } = useGetProjects();

    const softwaresIntegrations = useGetSoftwares();

    const imputationRawDimensions = useContext(ImputationDimensionsContext);

    const {
        data: inscriptionsData,
        isLoading: areInscriptionsLoading,
        cacheKey: queryCacheKey,
    } = useGetAllInscriptions({
        budgetName: selectedBudgetName ?? '',
        exercice: year,
        budgetBy: budgetProposals[0].by,
        phase: budgetProposals[0].phase,
        expenseOrIncome: expenseOrIncomeValue,
    });

    const imputationAsString = getImputationAsString(imputationRawDimensions);

    const isLoading = areInscriptionsLoading || !year || isProjectLoading;

    const columns = !isLoading
        ? getOrderedColumns([
              ...getInscriptionsGridColumns(
                  t,
                  budgetProposals,
                  imputationAsString,
                  true,
                  projects,
                  year,
              ),
              ...getAllInscriptionsGridColumns(t, softwaresIntegrations),
          ])
        : [];

    const inscriptions = React.useMemo(
        () => (inscriptionsData ? inscriptionsData.data : undefined),
        [JSON.stringify(inscriptionsData)],
    );
    const sourceTable = useSelectSecondaryBudgetAxisSourceTable();
    const inscriptionsWithPermissionsAndPhase = buildInscriptionsWithPhase({
        inscriptions,
        budgetProposals,
    });

    const {
        showOrphanWarning,
        setShowOrphanWarning,
        showWanderingWarning,
        setShowWanderingWarning,
        orphanWarningMessage,
        wanderingWarningMessage,
    } = useInscriptionAlertsManager(inscriptions, budgetProposals);

    const tabsList = [
        {
            label: t('budget.expense'),
            pathname: `/budget_overview/${tab}/expense`,
        },
        {
            label: t('budget.income'),
            pathname: `/budget_overview/${tab}/income`,
        },
    ].map((tabItem) => {
        const exportConfig = {
            fileName: getExcelFilename(ALL_INSCRIPTION_FILE_PREFIX, [
                expenseOrIncomeUrlParams,
            ]),
            sheetName: t('budgetOverview.allInscriptions'),
        };
        return {
            ...tabItem,
            key: tabItem.label,
            tabActions: (
                <TabActions
                    trackedEvents={
                        TRACKABLE_EVENTS.budget.budgetOverview.inscriptions
                    }
                    hideEmpty={false}
                    toggleLabel={t(
                        'budgetProposal.hideEmptySecondaryBudgetAxisElements',
                        { context: sourceTable },
                    )}
                    fileName={exportConfig.fileName}
                    showHistoryButton
                    setHistoryModalOpen={setIsInscriptionHistoryModalOpen}
                />
            ),
            children: isLoading ? (
                <Loading />
            ) : (
                <InscriptionsGrid
                    budgetProposals={budgetProposals}
                    expenseOrIncome={expenseOrIncomeValue}
                    pagination
                    paginationPageSize={15}
                    nbDisplayedInscriptions={15}
                    allowRowGroupStatusUpdate
                    allowFloatingFilters
                    inscriptions={inscriptionsWithPermissionsAndPhase}
                    columns={columns}
                    queryCacheKey={queryCacheKey}
                    showRowGroupPanel
                    excelExport={exportConfig}
                    settings={budgetUserSettings.allInscriptionsGrid.columns}
                    onSettingsUpdate={getAllInscriptionsUpdateBudgetSettingsCallback(
                        mutateSettings,
                    )}
                />
            ),
            disabled: false,
        };
    });

    return (
        <>
            <Box sx={{ margin: '0rem 3rem' }}>
                <InscriptionAlert
                    message={orphanWarningMessage}
                    isDisplayed={showOrphanWarning}
                    onClose={() => setShowOrphanWarning(false)}
                />
                <InscriptionAlert
                    message={wanderingWarningMessage}
                    isDisplayed={showWanderingWarning}
                    onClose={() => setShowWanderingWarning(false)}
                />
                <StyGrid>
                    <StyOveriddenTabulatedChip tabsList={tabsList} />
                </StyGrid>
            </Box>
            <InscriptionsHistoryModal
                isOpen={isInscriptionHistoryModalOpen}
                onClose={() => setIsInscriptionHistoryModalOpen(false)}
                title={inscriptionHistoryModalTitle}
                exercice={year}
                budgetName={selectedBudgetName || ''}
                budgetProposalBy={budgetProposals[0].by}
                expenseOrIncome={expenseOrIncomeValue}
            />
        </>
    );
}
